import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./employerForm.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");
const employerApi = axios.create({ baseURL: `${API_ROOT}/api` });

/* ─── Validation Rules ──────────────────────────────────────────────────────── */
const VALIDATORS = {
  firstName:    v => !v?.trim() ? "First name is required" : v.trim().length < 2 ? "At least 2 characters" : /[^a-zA-Z\s'-]/.test(v) ? "Only letters, spaces, hyphens & apostrophes" : null,
  lastName:     v => !v?.trim() ? "Last name is required" : v.trim().length < 2 ? "At least 2 characters" : /[^a-zA-Z\s'-]/.test(v) ? "Only letters, spaces, hyphens & apostrophes" : null,
  role:         v => !v ? "Please select your role" : null,
  email:        v => !v?.trim() ? "Work email is required" : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? "Enter a valid email address" : null,
  phone:        v => !v?.trim() ? "Phone number is required" : !/^[\+]?[\d\s\-\(\)]{7,15}$/.test(v.replace(/\s/g, "")) ? "Enter a valid phone number" : null,
  altPhone:     v => v && !/^[\+]?[\d\s\-\(\)]{7,15}$/.test(v.replace(/\s/g, "")) ? "Enter a valid phone number" : null,
  linkedin:     v => v && !/^https?:\/\/(www\.)?linkedin\.com\/.+/.test(v) ? "Must be a valid LinkedIn URL" : null,
  company:      v => !v?.trim() ? "Company name is required" : v.trim().length < 2 ? "At least 2 characters" : null,
  website:      v => v && !/^https?:\/\/.+\..+/.test(v) ? "Must be a valid URL starting with http:// or https://" : null,
  industry:     v => !v ? "Please select your industry" : null,
  size:         v => !v ? "Please select company size" : null,
  gst:          v => v && !/^[0-9A-Z]{15}$/.test(v.toUpperCase()) ? "GST must be exactly 15 alphanumeric characters" : null,
  year:         v => { if (!v) return null; const n = parseInt(v); return isNaN(n) || n < 1800 || n > new Date().getFullYear() ? `Enter a year between 1800 and ${new Date().getFullYear()}` : null; },
  branches:     v => { if (!v) return null; const n = parseInt(v); return isNaN(n) || n < 0 || n > 10000 ? "Enter a valid number (0–10000)" : null; },
  address:      v => !v?.trim() ? "Registered address is required" : null,
  city:         v => !v?.trim() ? "City is required" : /[^a-zA-Z\s\-']/.test(v) ? "Only letters allowed" : null,
  state:        v => !v?.trim() ? "State is required" : null,
  pin:          v => v && !/^\d{4,10}$/.test(v) ? "Enter a valid PIN/ZIP code" : null,
  country:      v => !v ? "Please select a country" : null,
  companyEmail: v => v && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? "Enter a valid email address" : null,
  positions:    v => { if (!v) return null; const n = parseInt(v); return isNaN(n) || n < 1 || n > 10000 ? "Enter a number between 1 and 10000" : null; },
  hiringBio:    v => v && v.length > 2000 ? "Maximum 2000 characters" : null,
  password:     v => !v ? "Password is required" : v.length < 8 ? "At least 8 characters" : !/(?=.*[A-Z])/.test(v) ? "Include at least one uppercase letter" : !/(?=.*\d)/.test(v) ? "Include at least one number" : !/(?=.*[!@#$%^&*])/.test(v) ? "Include at least one special character (!@#$%^&*)" : null,
  confirm:      (v, form) => !v ? "Please confirm your password" : v !== form.password ? "Passwords do not match" : null,
};

const REQUIRED_BY_STEP = [
  ["firstName", "lastName", "role", "email", "phone"],
  ["company", "industry", "size", "address", "city", "state", "country"],
  [],
  [],
  ["password", "confirm"],
];

const STEPS = ["Personal", "Company", "Recruiting", "Documents", "Account"];

/* ─── Shared UI Components ──────────────────────────────────────────────────── */
function StepBar({ current }) {
  return (
    <div className="step-bar">
      {STEPS.map((label, i) => (
        <div key={label} className={`step-item ${i < current ? "done" : ""} ${i === current ? "active" : ""}`}>
          <div className="step-circle">
            {i < current ? <span className="step-check">✓</span> : <span>{i + 1}</span>}
          </div>
          <span className="step-label">{label}</span>
          {i < STEPS.length - 1 && <div className={`step-line ${i < current ? "done" : ""}`} />}
        </div>
      ))}
    </div>
  );
}

function FloatingOrbs() {
  return (
    <div className="orbs" aria-hidden="true">
      <div className="orb orb-1" /><div className="orb orb-2" />
      <div className="orb orb-3" /><div className="orb orb-4" />
    </div>
  );
}

function Field({ label, required, hint, children, delay = 0, error, success }) {
  return (
    <div className="field" style={{ animationDelay: `${delay}ms` }}>
      <label className="field-label">
        {label}
        {required && <span className="field-req">*</span>}
        {hint && <span className="field-hint">{hint}</span>}
      </label>
      {children}
      {error   && <span className="field-error"><span className="field-err-icon">⚠</span>{error}</span>}
      {!error && success && <span className="field-success">✓ Looks good</span>}
    </div>
  );
}

function Input({ name, placeholder, type = "text", value, onChange, onBlur, hasError, isValid }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value || ""}
      onChange={e => onChange(name, e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onBlur && onBlur(name); }}
      className={`inp ${focused ? "inp-focused" : ""} ${hasError ? "has-error" : ""} ${isValid && !hasError ? "is-valid" : ""}`}
      autoComplete={type === "password" ? "new-password" : "off"}
    />
  );
}

function Select({ name, options, value, onChange, onBlur, hasError }) {
  const [focused, setFocused] = useState(false);
  return (
    <div className={`sel-wrap ${focused ? "sel-focused" : ""} ${hasError ? "has-error" : ""}`}>
      <select
        value={value || ""}
        onChange={e => onChange(name, e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur && onBlur(name); }}
        className="sel"
      >
        <option value="">Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <span className="sel-arrow">▾</span>
    </div>
  );
}

function Textarea({ name, placeholder, value, onChange, onBlur, hasError, maxLength = 2000 }) {
  const [focused, setFocused] = useState(false);
  const len = (value || "").length;
  return (
    <>
      <textarea
        placeholder={placeholder}
        value={value || ""}
        onChange={e => onChange(name, e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur && onBlur(name); }}
        className={`textarea ${focused ? "inp-focused" : ""} ${hasError ? "has-error" : ""}`}
      />
      <span className={`char-counter ${len > maxLength * 0.9 ? "near-limit" : ""} ${len > maxLength ? "over-limit" : ""}`}>
        {len}/{maxLength}
      </span>
    </>
  );
}

function CheckGroup({ name, options, value = [], onChange }) {
  const toggle = opt => {
    const next = value.includes(opt) ? value.filter(x => x !== opt) : [...value, opt];
    onChange(name, next);
  };
  return (
    <div className="check-group">
      {options.map(opt => (
        <label key={opt} className={`check-pill ${value.includes(opt) ? "checked" : ""}`}>
          <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function FileDropZone({ label, hint }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const ref = useRef();
  const handleDrop = e => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };
  return (
    <div
      className={`dropzone ${dragging ? "dragging" : ""} ${file ? "has-file" : ""}`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => ref.current.click()}
    >
      <input type="file" ref={ref} style={{ display: "none" }} onChange={e => setFile(e.target.files[0])} />
      <div className="dropzone-icon">{file ? "✅" : "☁"}</div>
      {file
        ? <p className="dropzone-name">{file.name}</p>
        : <><p className="dropzone-title">Drop {label} here</p><p className="dropzone-hint">{hint}</p></>
      }
    </div>
  );
}

function PasswordStrength({ password }) {
  const checks = [
    { label: "At least 8 characters",            met: password?.length >= 8 },
    { label: "One uppercase letter",             met: /[A-Z]/.test(password || "") },
    { label: "One number",                       met: /\d/.test(password || "") },
    { label: "One special character (!@#$%^&*)", met: /[!@#$%^&*]/.test(password || "") },
  ];
  const score = checks.filter(c => c.met).length;
  const levels = ["", "weak", "fair", "good", "strong"];
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  if (!password) return null;
  return (
    <div className="pwd-strength">
      <div className="pwd-bars">
        {[0, 1, 2, 3].map(i => <div key={i} className={`pwd-bar ${i < score ? levels[score] : ""}`} />)}
      </div>
      <span className={`pwd-strength-label ${levels[score]}`}>{labels[score]}</span>
      <div className="pwd-rules">
        {checks.map(c => (
          <div key={c.label} className={`pwd-rule ${c.met ? "met" : ""}`}>
            <div className="pwd-rule-dot" />{c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step Panels ───────────────────────────────────────────────────────────── */
function StepPersonal({ form, set, errors, touched, touch, emailExists, phoneExists }) {
  const e = n => touched[n] ? errors[n] : null;
  const v = n => touched[n] && !errors[n] && form[n];
  return (
    <div className="step-panel">
      <div className="panel-intro">
        <h2 className="panel-title">Tell us about <em>yourself</em></h2>
        <p className="panel-sub">Your point-of-contact details for the account</p>
      </div>
      <div className="grid-2">
        <Field label="First Name" required delay={50} error={e("firstName")} success={v("firstName")}>
          <Input name="firstName" placeholder="John" value={form.firstName} onChange={set} onBlur={touch} hasError={!!e("firstName")} isValid={!!v("firstName")} />
        </Field>
        <Field label="Last Name" required delay={100} error={e("lastName")} success={v("lastName")}>
          <Input name="lastName" placeholder="Smith" value={form.lastName} onChange={set} onBlur={touch} hasError={!!e("lastName")} isValid={!!v("lastName")} />
        </Field>
        <Field label="Job Title / Role" required delay={150} error={e("role")} success={v("role")}>
          <Select name="role" value={form.role} onChange={set} onBlur={touch} hasError={!!e("role")}
            options={["HR Manager", "HR Executive", "Recruitment Manager", "Talent Acquisition Specialist", "Hiring Manager", "Director of HR", "VP of Human Resources", "Business Owner / Founder", "Other"]} />
        </Field>
        <Field label="Department" delay={200}>
          <Input name="dept" placeholder="e.g. Human Resources" value={form.dept} onChange={set} />
        </Field>
        <Field label="Work Email" required delay={250} error={e("email")} success={v("email")}>
          <Input name="email" placeholder="you@company.com" type="email" value={form.email} onChange={set} onBlur={touch} hasError={!!e("email")} isValid={!!v("email")} />
          {emailExists && <span className="field-error">⚠ This email is already registered.</span>}
        </Field>
        <Field label="Phone Number" required delay={300} error={e("phone")} success={v("phone")}>
          <Input name="phone" placeholder="+91 98765 43210" type="tel" value={form.phone} onChange={set} onBlur={touch} hasError={!!e("phone")} isValid={!!v("phone")} />
          {phoneExists && <span className="field-error">⚠ This phone number is already registered.</span>}
        </Field>
        <Field label="Alternate Phone" delay={350} error={e("altPhone")} success={v("altPhone")}>
          <Input name="altPhone" placeholder="+91 98765 43210" type="tel" value={form.altPhone} onChange={set} onBlur={touch} hasError={!!e("altPhone")} isValid={!!v("altPhone")} />
        </Field>
        <Field label="LinkedIn Profile" delay={400} error={e("linkedin")} success={v("linkedin")}>
          <Input name="linkedin" placeholder="https://linkedin.com/in/yourprofile" type="url" value={form.linkedin} onChange={set} onBlur={touch} hasError={!!e("linkedin")} isValid={!!v("linkedin")} />
        </Field>
      </div>
    </div>
  );
}

function StepCompany({ form, set, errors, touched, touch }) {
  const e = n => touched[n] ? errors[n] : null;
  const v = n => touched[n] && !errors[n] && form[n];
  return (
    <div className="step-panel">
      <div className="panel-intro">
        <h2 className="panel-title">Your <em>company</em> details</h2>
        <p className="panel-sub">Help us verify your organisation identity</p>
      </div>
      <div className="grid-2">
        <Field label="Company Name" required delay={50} error={e("company")} success={v("company")}>
          <Input name="company" placeholder="Acme Corp" value={form.company} onChange={set} onBlur={touch} hasError={!!e("company")} isValid={!!v("company")} />
        </Field>
        <Field label="Company Website" delay={100} error={e("website")} success={v("website")}>
          <Input name="website" placeholder="https://www.company.com" type="url" value={form.website} onChange={set} onBlur={touch} hasError={!!e("website")} isValid={!!v("website")} />
        </Field>
        <Field label="Industry" required delay={150} error={e("industry")} success={v("industry")}>
          <Select name="industry" value={form.industry} onChange={set} onBlur={touch} hasError={!!e("industry")}
            options={["Information Technology", "Finance & Banking", "Healthcare", "E-Commerce / Retail", "Education", "Manufacturing", "Media & Entertainment", "Real Estate", "Logistics & Supply Chain", "Consulting", "Other"]} />
        </Field>
        <Field label="Company Size" required delay={200} error={e("size")} success={v("size")}>
          <Select name="size" value={form.size} onChange={set} onBlur={touch} hasError={!!e("size")}
            options={["1–10 employees", "11–50 employees", "51–200 employees", "201–500 employees", "501–1000 employees", "1000+ employees"]} />
        </Field>
        <Field label="Company Type" delay={250}>
          <Select name="companyType" value={form.companyType} onChange={set}
            options={["Private Limited", "Public Limited", "LLP", "Sole Proprietorship", "Partnership", "NGO / Non-profit", "Government"]} />
        </Field>
        <Field label="GST / Tax Reg. No." delay={300} error={e("gst")} success={v("gst")}>
          <Input name="gst" placeholder="22AAAAA0000A1Z5" value={form.gst} onChange={set} onBlur={touch} hasError={!!e("gst")} isValid={!!v("gst")} />
        </Field>
        <Field label="Year Established" delay={350} error={e("year")} success={v("year")}>
          <Input name="year" placeholder="2010" type="number" value={form.year} onChange={set} onBlur={touch} hasError={!!e("year")} isValid={!!v("year")} />
        </Field>
        <Field label="No. of Branches" delay={400} error={e("branches")} success={v("branches")}>
          <Input name="branches" placeholder="3" type="number" value={form.branches} onChange={set} onBlur={touch} hasError={!!e("branches")} isValid={!!v("branches")} />
        </Field>
      </div>
      <div className="grid-1" style={{ marginTop: 18 }}>
        <Field label="Registered Address" required delay={450} error={e("address")} success={v("address")}>
          <Input name="address" placeholder="Street / Building / Floor" value={form.address} onChange={set} onBlur={touch} hasError={!!e("address")} isValid={!!v("address")} />
        </Field>
      </div>
      <div className="grid-3" style={{ marginTop: 18 }}>
        <Field label="City" required delay={500} error={e("city")} success={v("city")}>
          <Input name="city" placeholder="Mumbai" value={form.city} onChange={set} onBlur={touch} hasError={!!e("city")} isValid={!!v("city")} />
        </Field>
        <Field label="State" required delay={550} error={e("state")} success={v("state")}>
          <Input name="state" placeholder="Maharashtra" value={form.state} onChange={set} onBlur={touch} hasError={!!e("state")} isValid={!!v("state")} />
        </Field>
        <Field label="PIN / ZIP" delay={600} error={e("pin")} success={v("pin")}>
          <Input name="pin" placeholder="400001" value={form.pin} onChange={set} onBlur={touch} hasError={!!e("pin")} isValid={!!v("pin")} />
        </Field>
      </div>
      <div className="grid-2" style={{ marginTop: 18 }}>
        <Field label="Country" required delay={650} error={e("country")} success={v("country")}>
          <Select name="country" value={form.country} onChange={set} onBlur={touch} hasError={!!e("country")}
            options={["India", "United States", "United Kingdom", "Canada", "Australia", "Other"]} />
        </Field>
        <Field label="Official Company Email" delay={700} error={e("companyEmail")} success={v("companyEmail")}>
          <Input name="companyEmail" placeholder="info@company.com" type="email" value={form.companyEmail} onChange={set} onBlur={touch} hasError={!!e("companyEmail")} isValid={!!v("companyEmail")} />
        </Field>
      </div>
    </div>
  );
}

function StepRecruiting({ form, set, errors, touched, touch }) {
  const e = n => touched[n] ? errors[n] : null;
  const v = n => touched[n] && !errors[n] && form[n];
  return (
    <div className="step-panel">
      <div className="panel-intro">
        <h2 className="panel-title">Recruitment <em>preferences</em></h2>
        <p className="panel-sub">What kind of talent are you looking for?</p>
      </div>
      <div className="grid-2">
        <Field label="Open Positions" delay={50} error={e("positions")} success={v("positions")}>
          <Input name="positions" placeholder="5" type="number" value={form.positions} onChange={set} onBlur={touch} hasError={!!e("positions")} isValid={!!v("positions")} />
        </Field>
        <Field label="Hiring Urgency" delay={100}>
          <Select name="urgency" value={form.urgency} onChange={set}
            options={["Immediately", "Within 1 Month", "1–3 Months", "3–6 Months", "No Urgency"]} />
        </Field>
      </div>
      <Field label="Types of Employment Offered" delay={150} hint="Select all that apply">
        <CheckGroup name="employmentTypes" value={form.employmentTypes} onChange={set}
          options={["Full-Time", "Part-Time", "Internship", "Contract", "Freelance", "Remote", "Hybrid"]} />
      </Field>
      <Field label="Preferred Candidate Experience" delay={200} hint="Select all that apply">
        <CheckGroup name="experience" value={form.experience} onChange={set}
          options={["Fresher (0–1 yr)", "Junior (1–3 yrs)", "Mid-Level (3–6 yrs)", "Senior (6–10 yrs)", "Lead / Manager (10+ yrs)"]} />
      </Field>
      <Field label="Brief About Hiring Needs" delay={250} error={e("hiringBio")}>
        <Textarea name="hiringBio" placeholder="Describe the roles you're hiring for, key skills, or specific requirements…"
          value={form.hiringBio} onChange={set} onBlur={touch} hasError={!!e("hiringBio")} />
      </Field>
    </div>
  );
}

function StepDocuments() {
  return (
    <div className="step-panel">
      <div className="panel-intro">
        <h2 className="panel-title">Upload <em>documents</em></h2>
        <p className="panel-sub">Secure your account with verified credentials</p>
      </div>
      <div className="grid-2 docs-grid">
        <Field label="Company Logo" delay={50}>
          <FileDropZone label="logo" hint="PNG or JPG · max 5 MB" />
        </Field>
        <Field label="Business Registration Certificate" delay={150}>
          <FileDropZone label="certificate" hint="PDF, PNG or JPG · max 10 MB" />
        </Field>
      </div>
      <div className="doc-note">
        <span className="doc-note-icon">🔒</span>
        All documents are encrypted and stored securely. They are used only for verification purposes.
      </div>
    </div>
  );
}

function StepAccount({ form, set, errors, touched, touch }) {
  const e = n => touched[n] ? errors[n] : null;
  const v = n => touched[n] && !errors[n] && form[n];
  return (
    <div className="step-panel">
      <div className="panel-intro">
        <h2 className="panel-title">Set up your <em>account</em></h2>
        <p className="panel-sub">Choose a strong password and a few final details</p>
      </div>
      <div className="grid-2">
        <Field label="Password" required delay={50} error={e("password")}>
          <Input name="password" placeholder="Min. 8 characters" type="password" value={form.password} onChange={set} onBlur={touch} hasError={!!e("password")} isValid={!!v("password")} />
          <PasswordStrength password={form.password} />
        </Field>
        <Field label="Confirm Password" required delay={100} error={e("confirm")} success={v("confirm")}>
          <Input name="confirm" placeholder="Re-enter password" type="password" value={form.confirm} onChange={set} onBlur={touch} hasError={!!e("confirm")} isValid={!!v("confirm")} />
        </Field>
      </div>
      <div className="grid-1" style={{ marginTop: 18 }}>
        <Field label="How did you hear about us?" delay={150}>
          <Select name="source" value={form.source} onChange={set}
            options={["Google Search", "Social Media", "Referral", "Job Fair / Event", "Email Campaign", "Other"]} />
        </Field>
      </div>
    </div>
  );
}

/* ─── Pending Approval Modal ────────────────────────────────────────────────── */
function PendingApprovalModal({ companyName, email, onRegisterAnother }) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // slight delay so the CSS transition fires properly
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  const copyEmail = () => {
    navigator.clipboard.writeText(email).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const timelineItems = [
    {
      state: "done",
      icon: "✓",
      label: "Account created",
      sub: "Your profile is saved securely",
    },
    {
      state: "active",
      icon: null,  // pulsing dot
      label: "Admin verification in progress",
      sub: "Our team reviews within 24–48 hours",
    },
    {
      state: "pending",
      icon: null,
      label: "Dashboard unlocked",
      sub: "Post jobs, search CVs, schedule interviews",
    },
    {
      state: "pending",
      icon: null,
      label: "Start hiring on TalentHive!",
      sub: "Full platform access granted",
    },
  ];

  return (
    <div className={`pa-overlay ${visible ? "pa-overlay--in" : ""}`}>
      <div className={`pa-modal ${visible ? "pa-modal--in" : ""}`}>

        {/* decorative top shimmer bar */}
        <div className="pa-top-bar" />

        {/* animated rings + icon */}
        <div className="pa-icon-wrap">
          <div className="pa-ring pa-ring--1" />
          <div className="pa-ring pa-ring--2" />
          <div className="pa-ring pa-ring--3" />
          <div className="pa-icon-core">
            {/* shield-check svg */}
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>
        </div>

        {/* header text */}
        <div className="pa-status-chip">
          <span className="pa-chip-dot" />
          Pending Admin Review
        </div>
        <h2 className="pa-title">Your Dashboard Is<br />Being Prepared</h2>
        <p className="pa-body">
          Your employer account for <strong>{companyName || "your company"}</strong> has been
          successfully submitted. Our admin team will review and verify your details before
          activating your full recruiter dashboard.
        </p>

        {/* timeline */}
        <div className="pa-timeline">
          {timelineItems.map((item, i) => (
            <div key={i} className={`pa-tl-row pa-tl-row--${item.state}`}>
              <div className="pa-tl-dot-col">
                {item.state === "done" && (
                  <div className="pa-tl-dot pa-tl-dot--done">{item.icon}</div>
                )}
                {item.state === "active" && (
                  <div className="pa-tl-dot pa-tl-dot--active">
                    <span className="pa-tl-pulse" />
                  </div>
                )}
                {item.state === "pending" && (
                  <div className="pa-tl-dot pa-tl-dot--pending" />
                )}
                {i < timelineItems.length - 1 && (
                  <div className={`pa-tl-connector ${item.state === "done" ? "pa-tl-connector--done" : ""}`} />
                )}
              </div>
              <div className="pa-tl-body">
                <div className="pa-tl-label">{item.label}</div>
                <div className="pa-tl-sub">{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* email note */}
        <div className="pa-email-row">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-email-icon">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <span className="pa-email-text">Approval notification will be sent to</span>
          <button className="pa-email-chip" onClick={copyEmail} title="Click to copy">
            {email}
            <span className="pa-copy-tag">{copied ? "Copied!" : "Copy"}</span>
          </button>
        </div>

        {/* what you unlock preview */}
        <div className="pa-features">
          <div className="pa-features-label">What unlocks after approval</div>
          <div className="pa-features-grid">
            {[
              ["📋", "Post Job Listings"],
              ["👥", "Search Candidate CVs"],
              ["🎥", "Online Interviews"],
              ["📊", "Analytics Dashboard"],
            ].map(([icon, label]) => (
              <div key={label} className="pa-feature-item">
                <span className="pa-feature-icon">{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* actions */}
        <div className="pa-actions">
          <a href="/employee/login" className="btn-primary pa-btn-login">
            Go to Login
            <span className="btn-arrow">→</span>
          </a>
          <button className="btn-secondary pa-btn-another" onClick={onRegisterAnother}>
            ← Register Another Employer
          </button>
        </div>

        <p className="pa-footer-note">
          Questions? Email us at{" "}
          <a href="mailto:support@talenthive.in" className="terms-link">
            support@talenthive.in
          </a>
        </p>
      </div>
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────────────────────────── */
export default function EmployerSignup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [terms, setTerms] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [stepError, setStepError] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);

  /* NEW: instead of `submitted` boolean → show the pending modal */
  const [pendingModal, setPendingModal] = useState(false);
  const [submittedData, setSubmittedData] = useState({ company: "", email: "" });

  const cardRef = useRef();

  const computeErrors = useCallback((f) => {
    const errs = {};
    Object.keys(VALIDATORS).forEach(k => {
      const err = VALIDATORS[k](f[k], f);
      if (err) errs[k] = err;
    });
    return errs;
  }, []);

  // live uniqueness checks
  useEffect(() => {
    if (!form.email || computeErrors(form).email) { setEmailExists(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await employerApi.get(`/employer/check-email?email=${encodeURIComponent(form.email)}`);
        setEmailExists(data.exists);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.email]);

  useEffect(() => {
    if (!form.phone || computeErrors(form).phone) { setPhoneExists(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await employerApi.get(`/employer/check-phone?phone=${encodeURIComponent(form.phone)}`);
        setPhoneExists(data.exists);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.phone]);

  const errors = computeErrors(form);

  const setField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    if (name === "password") setTouched(t => ({ ...t, confirm: true }));
  };

  const touchField = name => setTouched(t => ({ ...t, [name]: true }));

  const touchAll = fields => {
    const next = {};
    fields.forEach(f => (next[f] = true));
    setTouched(t => ({ ...t, ...next }));
  };

  const transition = dir => {
    setExiting(true);
    setTimeout(() => {
      setStep(s => s + dir);
      setExiting(false);
      setStepError("");
      cardRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 260);
  };

  const handleNext = () => {
    const required = REQUIRED_BY_STEP[step];
    touchAll(required);
    if (required.some(f => errors[f])) {
      setStepError("Please fix the errors above before continuing.");
      return;
    }
    setStepError("");
    transition(1);
  };

const handleSubmit = async () => {
  const allRequired = [
    "firstName","lastName","role","email","phone",
    "company","industry","size","address","city","state","country",
    "password","confirm",
  ];
  touchAll(allRequired);
  if (allRequired.some(f => errors[f])) {
    setStepError("Please fix the errors before submitting.");
    return;
  }
  if (emailExists) { setStepError("That email is already registered."); return; }
  if (phoneExists) { setStepError("That phone number is already registered."); return; }
  if (!terms)      { setStepError("Please accept the Terms & Conditions to continue."); return; }
 
  try {
    // ── POST to /api/employer/signup ──────────────────────────
    const { data } = await employerApi.post("/employer/signup", {
      firstName:       form.firstName,
      lastName:        form.lastName,
      role:            form.role,
      dept:            form.dept,
      email:           form.email,
      phone:           form.phone,
      altPhone:        form.altPhone,
      linkedin:        form.linkedin,
      company:         form.company,
      website:         form.website,
      industry:        form.industry,
      size:            form.size,
      companyType:     form.companyType,
      gst:             form.gst,
      year:            form.year,
      branches:        form.branches,
      address:         form.address,
      city:            form.city,
      state:           form.state,
      pin:             form.pin,
      country:         form.country,
      companyEmail:    form.companyEmail,
      positions:       form.positions,
      urgency:         form.urgency,
      employmentTypes: form.employmentTypes,
      experience:      form.experience,
      hiringBio:       form.hiringBio,
      password:        form.password,
      source:          form.source,
    });

    const token = data.token || "";
    localStorage.setItem("th_employer_token", token);
    localStorage.setItem("th_token",          token);
    localStorage.setItem("token",             token);
 
    // Store employer object under all keys the dashboard reads
    const employerObj = data.user || {};
    localStorage.setItem("th_employer", JSON.stringify({
      ...employerObj,
      status:  employerObj.status  || "pending",
      company: employerObj.company || form.company,
    }));
    localStorage.setItem("th_user", JSON.stringify(employerObj));
    localStorage.setItem("user",    JSON.stringify(employerObj));
 
    // ── Show pending approval modal ───────────────────────────
    setSubmittedData({ company: form.company, email: form.email });
    setPendingModal(true);
 
  } catch (err) {
    const msg = err.response?.data?.error || "Signup failed. Please try again.";
    setStepError(msg);
  }
};

  const handleReset = () => {
    setPendingModal(false);
    setForm({});
    setTouched({});
    setTerms(false);
    setStep(0);
    setStepError("");
    setSubmittedData({ company: "", email: "" });
    // clear localStorage so a truly new registration starts fresh
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const stepProps = { form, set: setField, errors, touched, touch: touchField };

  const panels = [
    <StepPersonal {...stepProps} emailExists={emailExists} phoneExists={phoneExists} />,
    <StepCompany  {...stepProps} />,
    <StepRecruiting {...stepProps} />,
    <StepDocuments />,
    <StepAccount  {...stepProps} />,
  ];

  const isLast = step === STEPS.length - 1;

  return (
    <>
      <div className="er-root">
        <FloatingOrbs />
        <div className="er-layout">
          <aside className="er-sidebar">
            <div className="sidebar-logo">
              <div className="logo-mark">💼</div>
              <div>
                <div className="logo-name text-nowrap">Talent Hive</div>
                <div className="logo-sub">Employer Registration</div>
              </div>
            </div>
            <StepBar current={step} />
          </aside>

          <main className="er-card" ref={cardRef}>
            <div className="card-header">
              <div className="ch-step-count">Step {step + 1} of {STEPS.length}</div>
              <div className="ch-progress-wrap">
                <div className="ch-progress-bar" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            </div>

            <div className={`panel-wrapper ${exiting ? "panel-exit" : "panel-enter"}`}>
              {panels[step]}
            </div>

            {stepError && (
              <div className="step-error-banner">⚠ {stepError}</div>
            )}

            {isLast && (
              <div className="terms-box">
                <label className="terms-label">
                  <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} className="terms-cb" />
                  <span>
                    I agree to the <a href="#" className="terms-link">Terms &amp; Conditions</a> and{" "}
                    <a href="#" className="terms-link">Privacy Policy</a>. I confirm all information is accurate
                    and I am authorised to represent my company.
                  </span>
                </label>
              </div>
            )}

            <div className="card-nav">
              {step > 0
                ? <button className="btn-back" onClick={() => transition(-1)}>← Back</button>
                : <div />
              }
              {isLast
                ? <button className="btn-primary" onClick={handleSubmit}>
                    Create Employer Account <span className="btn-arrow">→</span>
                  </button>
                : <button className="btn-primary" onClick={handleNext}>
                    Continue <span className="btn-arrow">→</span>
                  </button>
              }
            </div>

            <p className="signin-prompt">
              Already have an account?
              <a href="/employee/login" className="signin-link">Sign in here</a>
            </p>
          </main>
        </div>
      </div>

      {/* ── Pending Approval Modal — rendered outside the form card ── */}
      {pendingModal && (
        <PendingApprovalModal
          companyName={submittedData.company}
          email={submittedData.email}
          onRegisterAnother={handleReset}
        />
      )}
    </>
  );
}
